import {
  FC,
  useState,
  useCallback,
  useMemo,
  HTMLAttributes,
  useEffect,
} from 'react'
import { styled, Grid2, Snackbar, Alert } from '@mui/material'
import { useForm } from '@/utilities/index'
import {
  Searchbar,
  MaintenanceTicketsForProcessingTable,
  MaintenanceTicketsProcessedTable,
  FormStepper,
  FilterButton,
  MaintenanceTicketsBmProcessedTable,
  AuditTrailModal,
  FilterTicketsModal,
} from '@/components/index'
import FileTicketIcon from '@/assets/icons/file-ticket.svg'
import { MaintenanceTabType } from '@/utilities/types/filters'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  maintenanceTicketFilterConfig,
  ticketsMaintenanceFilterConfig,
} from '@/utilities/constants/filterConfigs'
import type {
  TicketData,
  TicketsData,
  AuditTrailData,
} from '@/utilities/types/table'
import type { FilterConfig, FilterState } from '@/utilities/types/shared-filter'
import { useMaintenanceTicketsContext } from '@/context/MaintenanceTicketsContext'
import { FormStepsContextProvider } from '@/context/FormStepsContext'
import ViewTicketModal from '@/components/modal/ViewTicketModal'
import FilterIcon from '@/assets/icons/filter.svg'
import { StepperType } from '@/utilities/types/ticket'
import { useAlert } from '@/utilities/useAlert'
import {
  generateCsv,
  generatePdf,
  syncAllData,
} from '@/api/maintenance-tickets-api'
import theme from '@/utilities/Theme'
import BasicTable from '@/components/table/BasicTable'
import SyncIcon from '@/assets/icons/sync.svg'

interface ButtonTabProps {
  buttontype: MaintenanceTabType
  selected: MaintenanceTabType
}

interface ContainerProps extends HTMLAttributes<HTMLDivElement> {
  inert?: string
}

// const ActionButton = styled('button')(({ theme }) => ({
//   display: 'flex',
//   alignItems: 'center',
//   justifyContent: 'center',
//   gap: '8px',
//   width: '150px',
//   height: '42px',
//   borderRadius: '6px',
//   background: theme.palette.primary.main,
//   color: theme.palette.common.white,
//   fontFamily: theme.typography.fontFamily,
//   fontSize: '12px',
//   fontWeight: 600,
//   border: 'none',
//   cursor: 'pointer',
// }))

const Container = styled('div')<ContainerProps>(({ theme }) => ({
  width: '100%',
  height: '100%',
  color: theme.palette.text.primary,
  background: 'transparent',
  overflowX: 'hidden',
  overflowY: 'auto',
}))

const FileButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  width: '144px',
  height: '42px',
  borderRadius: '6px',
  background: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontFamily: theme.typography.fontFamily,
  fontSize: '12px',
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
  [theme.breakpoints.down('md')]: {
    width: '98%',
  },
}))

const ReportButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  width: '144px',
  height: '42px',
  borderRadius: '6px',
  background: theme.palette.background.paper,
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
  fontSize: '12px',
  fontWeight: 600,
  border: `1px solid ${theme.palette.grey[500]}`,
  cursor: 'pointer',
  [theme.breakpoints.down('md')]: {
    width: '98%',
  },
  '&:hover': {
    background: theme.palette.grey[200],
  },
}))

const ButtonTab = styled('button')<ButtonTabProps>(
  ({ theme, buttontype, selected }) => ({
    width: buttontype === 'Processed' ? '87px' : '114px',
    height: '45px',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    border: `1px solid ${theme.palette.grey[400]}`,
    background:
      buttontype === selected ? theme.palette.secondary.main : 'white',
    color:
      buttontype === selected
        ? theme.palette.common.white
        : theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: '12px',
    fontFamily: theme.typography.fontFamily,
    cursor: 'pointer',
    '&:focus': {
      outline: 'none',
    },
  }),
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
})

type CurrentFilterState = FilterState<TicketsData>

const initialFilterState: CurrentFilterState = {
  regularFilters: {},
  dateRanges: {
    createdDate: { start: null, end: null },
    modifiedDate: { start: null, end: null },
  },
  timeRange: { start: null, end: null },
  sort: {
    field: 'dateModified',
    direction: 'desc',
  },
}

export const MaintenanceTickets: FC = () => {
  const {
    processing,
    processed,
    processingState,
    processedState,
    selectedTab,
    setSelectedTab,
    searchTickets,
    role,
    bmProcessed,
    bmState,
    auditTrail,
    auditTrailState,
    viewTicketAuditTrail,
    resetAuditTrailFilter,
    dispatch,
    mutateBmProcessed,
    mutateForProcessing,
    viewQuotationForm,
    filterTicketsMethodRepair,
    filterTicketsStatus,
    filterTicketsTypeConcern,
    filterTickets,
    resetFilters,
    filterSortedTickets,
    actions: {
      updateMaintenanceTicket,
      updateMaintenanceTicketByAc,
      createMaintenanceTicket,
      updateMaintenanceTicketByMe,
      updateMaintenanceTicketByMa,
    },
    viewPurchaseRequest,
    viewPurchaseOrder,
  } = useMaintenanceTicketsContext()

  const [filterModalOpen, setFilterModalOpen] = useState(false)

  const [filterState, setFilterState] =
    useState<CurrentFilterState>(initialFilterState)

  const { handleAlertMessage, ...alertRest } = useAlert()

  const [selectedType, setSelectedType] = useState<StepperType>('Create')

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [isGeneratingCsv, setIsGeneratingCsv] = useState(false)

  const handleSetType = (type: StepperType) => {
    setSelectedType(type)
  }

  const validatedData = useMemo(() => {
    switch (selectedTab) {
      case 'For BM':
        return bmProcessed || []
      case 'For Processing':
        return processing || []
      case 'Processed':
        return processed || []
      default:
        return []
    }
  }, [selectedTab, processing, processed, bmProcessed])

  const {
    formData: formUpdateData,
    showModal,
    handleAddData,
    handleUpdateData: handleUpdateModal,
    handleCloseModal,
  } = useForm()

  const {
    showModal: showTicketModal,
    handleDisplayData: handleDisplayTicketData,
    handleCloseModal: handleCloseTicketModal,
    formData: ticketData,
  } = useForm()

  const {
    showModal: showAuditTrail,
    handleAddData: handleDisplayAuditTrail,
    handleCloseModal: handleCloseAuditTrailModal,
  } = useForm()

  const handleSort = useCallback((field: string) => {
    setFilterState((prev) => ({
      ...prev,
      sort: {
        field: field as string,
        direction:
          prev.sort.field === field && prev.sort.direction === 'asc'
            ? 'desc'
            : 'asc',
      },
    }))
  }, [])

  const handleUpdateData = useCallback((data: any) => {
    console.log('Updating data:', data)
  }, [])

  const handleSearchProcessingTickets = (keyword: string) => {
    searchTickets('processing', keyword)
  }

  const handleSearchProcessedTickets = (keyword: string) => {
    searchTickets('processed', keyword)
  }

  const handleSearchBmProcessedTickets = (keyword: string) => {
    searchTickets('bm', keyword)
  }

  const handleViewAuditTrail = async (ticketId: number) => {
    console.log('clickerd here jay', ticketId)
    await viewTicketAuditTrail(ticketId)
    handleDisplayAuditTrail()
  }

  const handleUpdateModalShow = (data: any) => {
    setSelectedType('Update')
    handleUpdateModal(data)
  }

  const handleShowTicketModal = (data: any) => {
    handleDisplayTicketData(data)
    if (data.currentLayerId >= 4) {
      viewQuotationForm(data.id)
    }
    if (data.currentLayerId >= 14) {
      viewPurchaseRequest(data.id)
    }

    if (data.currentLayerId >= 15) {
      console.log('HERE')
      viewPurchaseOrder(data.id)
    }
  }

  const handleCloseViewTicketModal = () => {
    handleCloseTicketModal()
    viewQuotationForm(0)
    viewPurchaseOrder(0)
    viewPurchaseRequest(0)
  }

  const handleFilterSortedTickets = (
    sort: string,
    direction: 'asc' | 'desc',
  ) => {
    if (sort === 'ticketAgeinDays') {
      sort = 'age'
    }
    setFilterState((prev) => ({
      ...prev,
      sort: {
        field: sort as string,
        direction: direction,
      },
    }))
    filterSortedTickets(currTab, `${sort},${direction}`)
  }

  const renderTable = useMemo(() => {
    const baseProcessingProps = {
      isLoading: processingState.isLoading,
      onSort: handleSort,
      error: null,
      filterState,
      page: processingState.page,
      setPage: processingState.setPage,
      rowsPerPage: processingState.rowsPerPage,
      setRowsPerPage: processingState.setRowsPerPage,
      totalElem: processingState.totalElements,
      handleUpdate: handleUpdateData,
      handleDisplay: handleShowTicketModal,
      handleDisplayAudit: handleViewAuditTrail,
      handleSort: handleFilterSortedTickets,
    }

    const baseProcessedProps = {
      isLoading: processedState.isLoading,
      onSort: handleSort,
      error: null,
      filterState,
      page: processedState.page,
      setPage: processedState.setPage,
      rowsPerPage: processedState.rowsPerPage,
      setRowsPerPage: processedState.setRowsPerPage,
      totalElem: processedState.totalElements,
      handleUpdate: handleUpdateData,
      handleDisplay: handleShowTicketModal,
      handleDisplayAudit: handleViewAuditTrail,
      handleSort: handleFilterSortedTickets,
    }

    const baseBmProcessedProps = {
      isLoading: bmState.isLoading,
      onSort: handleSort,
      error: null,
      filterState,
      page: bmState.page,
      setPage: bmState.setPage,
      rowsPerPage: bmState.rowsPerPage,
      setRowsPerPage: bmState.setRowsPerPage,
      totalElem: bmState.totalElements,
      handleUpdate: handleUpdateData,
      handleDisplay: handleShowTicketModal,
      handleSort: handleFilterSortedTickets,
      handleDisplayAudit: handleViewAuditTrail,
    }

    switch (selectedTab) {
      case 'For BM':
        return (
          <MaintenanceTicketsBmProcessedTable
            {...baseBmProcessedProps}
            data={validatedData as TicketsData[]}
            role={role}
            handleUpdateModal={handleUpdateModalShow}
          />
        )
      case 'For Processing':
        return (
          <MaintenanceTicketsForProcessingTable
            {...baseProcessingProps}
            data={validatedData as TicketsData[]}
            role={role}
            handleUpdateModal={handleUpdateModalShow}
          />
        )
      case 'Processed':
        return (
          <MaintenanceTicketsProcessedTable
            {...baseProcessedProps}
            data={validatedData as TicketsData[]}
          />
        )
      default:
        return null
    }
  }, [
    selectedTab,
    validatedData,
    filterState,
    processed,
    processing,
    processedState,
    processingState,
    bmProcessed,
    bmState,
  ])

  const handleSelect = useCallback(
    (tabType: MaintenanceTabType) => {
      setSelectedTab(tabType)
      setFilterState(initialFilterState)
      setFilterModalOpen(false)
    },
    [setSelectedTab],
  )

  const handleFilterClick = useCallback(() => setFilterModalOpen(true), [])
  const handleFilterClose = useCallback(() => setFilterModalOpen(false), [])

  const handleApplyFilter = useCallback((newFilterState: FilterState) => {
    setFilterState(newFilterState)
    setFilterModalOpen(false)
  }, [])

  const handleCloseAuditModal = useCallback((type: string) => {
    handleCloseAuditTrailModal()
    resetAuditTrailFilter(type)
  }, [])

  let currTab: 'processing' | 'processed' | 'bm' = 'bm'

  switch (selectedTab) {
    case 'For BM':
      currTab = 'bm'
      break
    case 'For Processing':
      currTab = 'processing'
      break
    case 'Processed':
      currTab = 'processed'
      break
    default:
      currTab = 'bm'
      break
  }

  const handleReset = useCallback(() => {
    setFilterState(initialFilterState)

    filterTickets(currTab, {
      startCreatedDate: '',
      endCreatedDate: '',
      startModifiedDate: '',
      endModifiedDate: '',
    })
    filterTicketsMethodRepair(currTab, '')
    filterTicketsTypeConcern(currTab, '')
    filterTicketsStatus(currTab, '')

    resetFilters(currTab)
  }, [])

  const handleFilterTicketDates = (
    startCreatedDate: string,
    endCreatedDate: string,
    startModifiedDate: string,
    endModifiedDate: string,
  ) => {
    filterTickets(currTab, {
      startCreatedDate: startCreatedDate,
      endCreatedDate: endCreatedDate,
      startModifiedDate: startModifiedDate,
      endModifiedDate: endModifiedDate,
    })
  }

  const handleFilterTicketStatus = (status: string) => {
    filterTicketsStatus(currTab, status)
  }

  const handleFilterTicketMethodRepair = (method: string) => {
    filterTicketsMethodRepair(currTab, method)
  }

  const handleFilterTicketTypeConcern = (typeConcern: string) => {
    filterTicketsTypeConcern(currTab, typeConcern)
  }

  const handleGeneratePdfOrCsv = async (type: string) => {
    type === 'pdf' ? setIsGeneratingPdf(true) : setIsGeneratingCsv(true)
    try {
    let params = {}
    switch (selectedTab) {
      case 'For BM':
        params = {
          page: bmState.page,
          keyword: bmState.keyword,
          size: bmState.totalElements,
          sinceCreated: bmState.dateFilters.startCreatedDate,
          untilCreated: bmState.dateFilters.endCreatedDate,
          sinceModified: bmState.dateFilters.startModifiedDate,
          untilModified: bmState.dateFilters.endModifiedDate,
          status: bmState.status,
          methodOfRepair: bmState.methodOfRepair,
          typeOfConcern: bmState.typeOfConcern,
          sort: bmState.sort.split(',')[0],
          order: bmState.sort.split(',')[1],
          isProcessed: true,
          roleName: role,
        }
        break
      case 'For Processing':
        params = {
          page: processingState.page,
          keyword: processingState.keyword,
          size: processingState.totalElements,
          sinceCreated: processingState.dateFilters.startCreatedDate,
          untilCreated: processingState.dateFilters.endCreatedDate,
          sinceModified: processingState.dateFilters.startModifiedDate,
          untilModified: processingState.dateFilters.endModifiedDate,
          status: processingState.status,
          sort: processingState.sort.split(',')[0],
          order: processingState.sort.split(',')[1],
          methodOfRepair: processingState.methodOfRepair,
          typeOfConcern: processingState.typeOfConcern,
          isProcessed: false,
          roleName: role,
        }
        break
      case 'Processed':
        params = {
          page: processedState.page,
          keyword: processedState.keyword,
          size: processedState.totalElements,
          sinceCreated: processingState.dateFilters.startCreatedDate,
          untilCreated: processingState.dateFilters.endCreatedDate,
          sinceModified: processingState.dateFilters.startModifiedDate,
          untilModified: processingState.dateFilters.endModifiedDate,
          status: processedState.status,
          methodOfRepair: processedState.methodOfRepair,
          typeOfConcern: processedState.typeOfConcern,
          sort: processedState.sort.split(',')[0],
          order: processedState.sort.split(',')[1],
          isProcessed: true,
          roleName: role,
        }
        break
    }

    if (type === 'pdf') {
      await generatePdf(params)
    } else {
      await generateCsv(params)
    }
    } finally {
      type === 'pdf' ? setIsGeneratingPdf(false) : setIsGeneratingCsv(false)
    }
  }

  const handleSync = async () => {
    try {
      const response = await syncAllData()

      handleAlertMessage(response.data.message, 'success')
    } catch (e) {
      handleAlertMessage('Error syncing data.', 'error')
      console.error(e)
    }
  }

  useEffect(() => {
    const tableType =
      selectedTab === 'For Processing'
        ? 'forProcessingTickets'
        : 'processedTickets'
    const newFilterState = {
      ...maintenanceTicketFilterConfig[
        selectedTab === 'For Processing' ? 'forProcessing' : 'processed'
      ].defaultState,
      tableType,
    }
    setFilterState(newFilterState)
  }, [selectedTab, bmState])

  console.log('double check pagination', validatedData)

  return (
    <QueryClientProvider client={queryClient}>
      <Container inert={filterModalOpen ? 'true' : undefined}>
        {/* {(role === 'Super Admin' ||
          role === 'Maintenance Admin' ||
          role === 'Setup Area Supervisor') && (
          <Grid2
            container
            sx={{
              [theme.breakpoints.down('md')]: {
                display: 'block',
                marginTop: 1,
              },
            }}
          >
            <ActionButton sx={{ marginBottom: '10px' }} onClick={handleSync}>
              <img src={SyncIcon} alt="SyncProvinces" width={16} height={16} />
              Sync
            </ActionButton>
          </Grid2>
        )} */}

        <Grid2
          container
          justifyContent="space-between"
          alignItems="center"
          wrap="wrap"
          sx={{
            gap: 2,
            [theme.breakpoints.down('md')]: {
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 3,
            },
          }}
        >
          <Grid2 style={{ flexGrow: 1 }}>
            {selectedTab === 'For Processing' && (
              <Searchbar
                handleSearchKeyword={handleSearchProcessingTickets}
                keyword={processingState.keyword}
                // handleSearchNotifications={handleSearchNotifications}
              />
            )}
            {selectedTab === 'Processed' && (
              <Searchbar
                handleSearchKeyword={handleSearchProcessedTickets}
                keyword={processedState.keyword}
              />
            )}
            {selectedTab === 'For BM' && (
              <Searchbar
                handleSearchKeyword={handleSearchBmProcessedTickets}
                keyword={bmState.keyword}
              />
            )}
          </Grid2>
          <Grid2
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              [theme.breakpoints.down('md')]: {
                justifyContent: 'center',
              },
            }}
          >
            {(role === 'Branch Manager' ||
              role === 'OIC' ||
              role === 'Area Controller') && (
              <FileButton
                onClick={() => {
                  handleSetType('Create')
                  handleAddData()
                }}
              >
                <img src={FileTicketIcon} alt="File" width={16} height={16} />
                File New Ticket
              </FileButton>
            )}
            <ReportButton 
              disabled={isGeneratingPdf} 
              onClick={() => handleGeneratePdfOrCsv('pdf')}
              style={{
                opacity: isGeneratingPdf ? 0.6 : 1,
                cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
              }}
            >
              {isGeneratingPdf ? 'Generating PDF...' : 'Generate PDF'}
            </ReportButton>
            <ReportButton 
              disabled={isGeneratingCsv} 
              onClick={() => handleGeneratePdfOrCsv('csv')}
              style={{
                opacity: isGeneratingCsv ? 0.6 : 1,
                cursor: isGeneratingCsv ? 'not-allowed' : 'pointer',
              }}
            >
              {isGeneratingCsv ? 'Generating CSV...' : 'Generate CSV'}
            </ReportButton>
            <FilterButton
              onClick={handleFilterClick}
              icon={FilterIcon}
              width={16}
              height={16}
            />
          </Grid2>
        </Grid2>

        <Grid2 container flexDirection="column">
          {role !== 'Branch Manager' &&
            role !== 'OIC' &&
            role !== 'Super Admin' && (
              <Grid2
                display="flex"
                gap={1}
                mt={1}
                sx={{ transform: 'translateY(7.5px)', zIndex: 0 }}
              >
                <ButtonTab
                  buttontype="For Processing"
                  selected={selectedTab}
                  onClick={() => handleSelect('For Processing')}
                >
                  For Processing
                </ButtonTab>
                <ButtonTab
                  buttontype="Processed"
                  selected={selectedTab}
                  onClick={() => handleSelect('Processed')}
                >
                  Processed
                </ButtonTab>
              </Grid2>
            )}

          {/* <BasicTable /> */}

          {renderTable}
        </Grid2>

        {showModal && (
          <FormStepsContextProvider>
            <FormStepper
              show={showModal}
              handleClose={handleCloseModal}
              data={formUpdateData}
              type={selectedType}
              handleUpdateByBm={updateMaintenanceTicket}
              dispatch={dispatch}
              mutateBmProcessed={mutateBmProcessed}
              handleUpdateByAc={updateMaintenanceTicketByAc}
              mutateForProcessing={mutateForProcessing}
              handleCreateTicket={createMaintenanceTicket}
              bmState={bmState}
              handleUpdateByMe={updateMaintenanceTicketByMe}
              handleUpdateByMa={updateMaintenanceTicketByMa}
              handleAlertMessage={handleAlertMessage}
            />
          </FormStepsContextProvider>
        )}
        {showTicketModal && (
          <ViewTicketModal
            title="View Ticket"
            open={showTicketModal}
            onClose={handleCloseViewTicketModal}
            data={ticketData || null}
            role={role}
            tab={selectedTab}
          />
        )}
        {showAuditTrail && (
          <AuditTrailModal
            title="Audit Trail"
            open={showAuditTrail}
            onClose={handleCloseAuditModal}
            isLoading={auditTrailState.isLoading}
            page={auditTrailState.page}
            rowsPerPage={auditTrailState.rowsPerPage}
            totalElem={auditTrailState.totalElements}
            dateFilters={auditTrailState.dateFilters}
            data={auditTrail as AuditTrailData[]}
          />
        )}

        <FilterTicketsModal<TicketData>
          open={filterModalOpen}
          onClose={handleFilterClose}
          onApplyFilter={handleApplyFilter}
          initialFilters={filterState}
          config={ticketsMaintenanceFilterConfig() as FilterConfig<TicketData>}
          tab={''}
          handleAlertMessage={handleAlertMessage}
          handleReset={handleReset}
          handleFilterTicketDates={handleFilterTicketDates}
          handleFilterTicketMethodRepair={handleFilterTicketMethodRepair}
          handleFilterTicketTypeConcern={handleFilterTicketTypeConcern}
          handleFilterTicketStatus={handleFilterTicketStatus}
        />
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          open={alertRest.showAlert}
          onClose={alertRest.handleCloseAlert}
          autoHideDuration={3000}
        >
          <Alert
            onClose={alertRest.handleCloseAlert}
            elevation={6}
            variant="filled"
            severity={alertRest.alertSeverity}
          >
            {alertRest.alertMessage}
          </Alert>
        </Snackbar>
      </Container>
    </QueryClientProvider>
  )
}
